import React, {useCallback, useEffect, useRef, useState} from "react";
import {CloseOutlined, PlusOutlined, ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import {Button, Col, Form, Input, message, PageHeader, Row, Select, Spin, Table} from "antd";
import {useUserPermissions} from "../auth/userHooks";
import {getScheduledDTO} from "../api/scheduledApi";
import _ from "lodash";
import moment from "moment";
import sorters from "../util/sorters";
import {ScheduledEditButton} from "./ScheduledEditButton";
import {PREFERRED_DATE_FORMAT, PREFERRED_DATETIME_FORMAT} from "../util/time";
import {getPaginationConfig} from "../util/showAllPagination";
import {useDynamicTableHeight} from "../util/useDynamicTableHeight";
import {get} from "../api";

const Scheduled = () => {
  const [form] = Form.useForm();
  const [scheduled, setScheduled] = useState([]);
  const [emails, setEmails] = useState([]);
  const [filteredScheduled, setFilteredScheduled] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const permissions = useUserPermissions();
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [resetKey, setResetKey] = useState(0);


  const [manageData, setManageData] = useState({
    capabilities: [],
    employees:[],
    employment_status: [],
    levels: [],
    locations: [],
    practices: [],
    titles: [],
    managers: [],
  });

  const availableStatuses = [
    ...new Set(
      scheduled
        .sort((a, b) => sorters.string(a.status, b.status))
        .map(s => s.status.trim())
        .filter(s => s !== "")
    )
  ];

  const availableColumns = [
    ...new Set(
      scheduled
        .sort((a, b) => sorters.string(a.column, b.column))
        .map(c => c.column.trim())
        .filter(c => c !== "")
    )
  ];

  const loadManageData = async () => {
    const res = await get(`/employee_manage_data`);
    res.managers = res.employees.filter(e => e.l >= 3);
    setManageData(res);
  };

  const loadScheduled = useCallback(async () => {
    try {
      setLoading(true)
      const scheduledDto = await getScheduledDTO()
      const scheduled = scheduledDto.scheduled.map((s) => ({
        id: s.a,
        employee_id: s.b,
        name: s.c,
        email: s.d,
        effective_date: s.e,
        column: s.f,
        value: s.g,
        status: s.h,
        previous: s.i,
        processed_at: s.j
      }));
      setScheduled(scheduled);
      setEmails(scheduledDto.emails)
      filterScheduled(
        scheduled,
        searchText,
        selectedStatuses,
        selectedColumns
      );

    } catch (e) {
      console.log(e);
      message.error("Could not get scheduled data");
    } finally {
      setLoading(false)
    }
  }, [searchText, selectedStatuses, selectedColumns]);

  const filterScheduled = (
    scheduled,
    searchText,
    selectedStatuses,
    selectedColumns
  ) => {
    setFilteredScheduled(
      _.chain(scheduled)
        .filter(
          (u) =>
            (searchText === "" || _.includes(_.toLower(u.name) +
              _.toLower(u.value)
              , _.toLower(searchText))) &&
            (selectedStatuses.length === 0 || selectedStatuses.includes(u.status)) &&
            (selectedColumns.length === 0 || selectedColumns.includes(u.column))
        ).value()
    )
  };

  const search = (text) => {
    setSearchText(text);
    filterScheduled(scheduled, text, selectedStatuses, selectedColumns);
  }

  useEffect(() => {
    loadScheduled();
    loadManageData();
  }, [loadScheduled]);

  const clearForm = () => {
    setSearchText("");
    setSelectedStatuses([]);
    setSelectedColumns([]);
    form.resetFields();
    setResetKey(resetKey + 1);
    filterScheduled(scheduled, "", [], []);
  }

  const handleRecordUpdate = (id, updatedFields) => {
    const newScheduled = scheduled.map(r =>
      r.ID === id ? {...r, ...updatedFields} : r
    );
    setScheduled(newScheduled);
    filterScheduled(newScheduled, searchText, selectedStatuses, selectedColumns);
  };

  const renderMulti = (column, value) => {
    switch (column) {
      case "start_date":
      case "term_date":
        if (value !== null && value !== "") {
          return moment(value).utc(false).format(PREFERRED_DATE_FORMAT);
        }
        return "";
      case "manager_id":
      case "career_coach_id":
        return emails[value];
      default:
        return value;
    }
  };


  const columns = [
    {
      title: "Name",
      width: 200,
      render: (s) => s.name,
      sorter: (a, b) => sorters.string(a.name, b.name)
    },
    {
      title: "Email",
      width: 300,
      render: (s) => s.email,
      sorter: (a, b) => sorters.string(a.email, b.email)
    },
    {
      title: "Effective Date",
      width: 125,
      dataIndex: "effective_date",
      defaultSortOrder: "ascend",
      render: (ed) => moment(ed, 'YYYY-MM-DD').format(PREFERRED_DATE_FORMAT),
      sorter: (a, b) => sorters.dateIndex(a.effective_date, a.id, b.effective_date, b.id)
    },
    {
      title: "Column",
      width: 150,
      dataIndex: "column",
      sorter: (a, b) => sorters.string(a.column, b.column),
      render: (c) => {
        switch (c) {
          case "manager_id":
            return "manager";
          case "career_coach_id":
            return "career_coach";
          default:
            return c;
        }
      }
    },
    {
      title: "Value",
      width: 170,
      ellipsis: true,
      render: (r) => renderMulti(r.column, r.value),
    },
    {
      title: "Status",
      width: 140,
      dataIndex: "status",
      sorter: (a, b) => sorters.string(a.status, b.status),
      render: (s) => {
        let color;
        switch (s) {
          case "completed":
            color = "green";
            break;
          case "scheduled":
            color = "black";
            break;
          default:
            color = "red";
            break;
        }
        return (<div style={{color: color}}>{s}</div>)
      }
    },
    {
      title: "Processed",
      width: 170,
      dataIndex: "processed_at",
      render: (p) => p === null ? "" : moment(p).format(PREFERRED_DATETIME_FORMAT),
      sorter: (a, b) => sorters.date(a.processed_at, b.processed_at)
    },
    {
      title: "Previous",
      width: 170,
      ellipsis: true,
      render: (r) => renderMulti(r.column, r.previous),
      sorter: (a, b) => sorters.string(a.previous, b.previous)
    }
  ];
  if (permissions != null && permissions.canManageScheduled) {
    columns.push({
      title: "Action",
      width: 100,
      render: (r) => (
        <ScheduledEditButton
          record={r}
          onRefresh={loadScheduled}
          onRecordUpdated={handleRecordUpdate}
          manageData={manageData}
          // editModalVisible={editModalVisible}
          // setEditModalVisible={setEditModalVisible}
        />
      )
    });
  }

  return (
    <>
      <Spin spinning={loading}>
        <Row>
          <Col span={12}>
            <PageHeader title="Effective Dating"
                        subTitle={`(${filteredScheduled.length} of ${scheduled.length} shown)`}
                        style={{padding: 0}}/>
          </Col>

        </Row>
        <Form key={resetKey} form={form}>
          <Row style={{marginBottom: 10}}>
            <Col span={6} style={{marginRight: 10}}>
              <Form.Item>
                <Input
                  placeholder="Search..."
                  prefix={<SearchOutlined/>}
                  onChange={(e) => search(e.target.value)}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={3} style={{marginRight: 10}}>
              <Form.Item>
                <Select
                  style={{width: "100%"}}
                  loading={loading}
                  maxTagCount={0}
                  maxTagPlaceholder={`${selectedStatuses.length} Status${
                    selectedStatuses.length === 1 ? "" : "es"
                  } Selected`}
                  showArrow
                  mode="multiple"
                  placeholder="Statuses"
                  onChange={(values) => {
                    setSelectedStatuses(values);
                    filterScheduled(scheduled, searchText, values, selectedColumns);
                  }}
                >
                  {availableStatuses.map(s => (
                    <Select.Option key={s} value={s}>
                      {s}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item>
                <Select
                  style={{width: "100%"}}
                  loading={loading}
                  maxTagCount={0}
                  maxTagPlaceholder={`${selectedColumns.length} Column${
                    selectedColumns.length === 1 ? "" : "s"
                  } Selected`}
                  showArrow
                  mode="multiple"
                  placeholder="Columns"
                  onChange={(values) => {
                    setSelectedColumns(values);
                    filterScheduled(scheduled, searchText, selectedStatuses, values);
                  }}
                >
                  {availableColumns.map(c => (
                    <Select.Option key={c} value={c}>
                      {c}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={2}>
              <Button
                title="Clear filters"
                icon={<CloseOutlined/>}
                type="link"
                onClick={clearForm}
              >
                Clear filters
              </Button>
            </Col>
            <Col flex="auto">
              <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8}}>
                <Button
                  style={{float: "right"}}
                  icon={<PlusOutlined/>}
                  onClick={() => {
                    console.log("create");
                  }}
                >
                  Create New Record
                </Button>
                <Button
                  style={{float: "right"}}
                  loading={loading}
                  onClick={() => loadScheduled()}
                  icon={<ReloadOutlined/>}
                >
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
        <Table
          ref={tableContainerRef}
          rowKey="id"
          dataSource={filteredScheduled}
          columns={columns}
          pagination={getPaginationConfig(filteredScheduled.length)}
          size="small"
          scroll={{x: "100%", y: tableHeight}}
          loading={loading}
        />

      </Spin>

    </>
  );
};

export {Scheduled};
